import { Box, Button, Badge } from '@chakra-ui/react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { IconProp } from '@fortawesome/fontawesome-svg-core';
import {
  faCircle,
  faCalendar,
  faListCheck,
  faFile,
  faFileInvoiceDollar,
  faFileCheck,
  faClipboardList,
  faPassport,
  faPlaneDeparture,
  faBallot,
  faCalendarLinesPen,
  faHeadset
} from '@fortawesome/pro-regular-svg-icons';
import { ISectionProps, SectionId, Status } from './types';
import { TravelStatusEnum } from 'types';
import { EnrolmentStatusEnum } from '../../../store/student/offer/types';
import { slug } from '../../../utilities/formatting';

type Props = ISectionProps & {
  onSectionSelect?: (sectionId: SectionId) => void;
  onClose: () => void;
  isActive: boolean;
};

type SettingsMap = Record<
  string,
  {
    icon?: IconProp;
    iconAlt?: string;
    colorScheme: string;
    hidden?: boolean;
    badge?: string;
  }
>;

function NavButton({ id, label, status, onSectionSelect, clickable, onClose, isActive }: Props) {
  const navIcon = (id: SectionId) => {
    switch (id) {
      case SectionId.CHECKLIST:
        return faListCheck;
      case SectionId.GENERAL_DOC:
        return faFileCheck;
      case SectionId.FINANCE_DOC:
        return faFileInvoiceDollar;
      case SectionId.PRE_CAS_INTERVIEW:
        return faClipboardList;
      case SectionId.SCHEDULED_INTERVIEWS:
        return faHeadset;
      case SectionId.VISA_PREP:
        return faBallot;
      case SectionId.ENROLMENT:
        return faCalendarLinesPen;
      case SectionId.PRE_DEPARTURE_DOCS:
        return faPlaneDeparture;
      case SectionId.AFTER_TRAVEL_DOCS:
        return faPassport;
      case SectionId.CAS_DOC:
        return faFile;
      case SectionId.COMPLETE:
        return faCalendar;
      default:
        return faCircle;
    }
  };

  const settingsMap: SettingsMap = {
    Active: {
      colorScheme: 'violet',
      badge: 'Active'
    },
    [Status.Disabled]: {
      colorScheme: 'gray'
    },
    [Status.Completed]: {
      colorScheme: 'green',
      badge: 'Done'
    },
    [Status.Available]: {
      colorScheme: 'cyan',
      badge: 'New'
    },
    [Status.InProgress]: {
      colorScheme: 'orange',
      badge: 'To do'
    },
    [Status.Invalid]: {
      colorScheme: 'red',
      badge: 'Attention'
    },
    [Status.Final]: {
      colorScheme: 'gray',
      hidden: true
    },
    [Status.Locked]: {
      colorScheme: 'green',
      badge: 'Locked'
    },
    [TravelStatusEnum.None]: {
      colorScheme: 'orange',
      badge: 'To do'
    },
    [TravelStatusEnum.Booked]: {
      colorScheme: 'orange',
      badge: 'Booked'
    },
    [TravelStatusEnum.TravelPrevented]: {
      colorScheme: 'orange',
      badge: 'Update'
    },
    [TravelStatusEnum.TravelCancelled]: {
      colorScheme: 'orange',
      badge: 'Update'
    },
    [TravelStatusEnum.NoBookingYet]: {
      colorScheme: 'orange',
      badge: 'Update'
    },
    [TravelStatusEnum.Arrived]: {
      colorScheme: 'green',
      badge: 'Arrived'
    },
    [TravelStatusEnum.AlreadyInUk]: {
      colorScheme: 'green',
      badge: 'In UK'
    },
    [EnrolmentStatusEnum.None]: {
      colorScheme: 'orange',
      badge: 'To do'
    },
    [EnrolmentStatusEnum.Question]: {
      colorScheme: 'orange',
      badge: 'Update'
    },
    [EnrolmentStatusEnum.Wait]: {
      colorScheme: 'orange',
      badge: 'Update'
    },
    [EnrolmentStatusEnum.OK]: {
      colorScheme: 'green',
      badge: 'Done'
    },
    [EnrolmentStatusEnum.Cancel]: {
      colorScheme: 'gray',
      badge: 'Visa refusal'
    },
    Withdraw: {
      colorScheme: 'gray',
      badge: 'Withdraw'
    },
    Defer: {
      colorScheme: 'gray',
      badge: 'Defer'
    }
  };

  const settings = isActive ? settingsMap.Active : settingsMap[status];
  const dataId = slug(id.toLowerCase());

  const navItemOptions = {
    icon: isActive ? settingsMap[status].icon : settings.icon,
    alt: settingsMap[status].iconAlt,
    colorScheme: settingsMap[status].colorScheme,
    hidden: settingsMap[status].hidden,
    badge: settingsMap[status].badge
  };

  return (
    <Button
      data-selector={`section-${dataId}`}
      variant="subnav"
      isActive={isActive}
      isDisabled={!clickable}
      hidden={navItemOptions.hidden}
      onClick={() => {
        if (typeof onSectionSelect === 'function') {
          onSectionSelect(id);
        }
        onClose();
      }}
      leftIcon={<FontAwesomeIcon icon={navIcon(id)} fixedWidth className="nav-icon" size="lg" />}
      height={'navbarHeight'}
      px={3}
    >
      <Box as="span" flexGrow="1" overflow="hidden" whiteSpace="nowrap" textOverflow="ellipsis" ml={1}>
        {label}
      </Box>

      {navItemOptions.badge ? (
        <Badge
          data-selector={`progress-${navItemOptions.badge}`}
          variant="subtle"
          colorScheme={navItemOptions.colorScheme}
          fontWeight="medium"
          ml={2}
        >
          {navItemOptions.badge}
        </Badge>
      ) : null}
    </Button>
  );
}

export default NavButton;
